// @react
import React, { Fragment, ReactElement, useRef, useState } from 'react'
// @typings
import {
  EducationField,
  EducationFormValues,
} from 'modules/common/forms/types/education'
import { GetConstantsQueryResult } from 'lib/graphql/graphqlTypes'
// @libraries
import {
  Control,
  Controller,
  useFieldArray,
  FieldErrors,
} from 'react-hook-form'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import Banner from '@bit/atd.web.banner'
import Select from '@bit/atd.web.select'
import Divider from '@bit/atd.web.divider'
import Button from '@bit/atd.web.button'
import RemoveModal from 'modules/common/components/RemoveModal'
import strings from 'lib/constants/strings'

type PropsType = {
  constantsData: GetConstantsQueryResult['data']
  control: Control<EducationFormValues>
  errors: FieldErrors<EducationFormValues>
  deleteField: (id: number, remove: (index?: number | number[]) => void) => void
  editing: boolean
}

const MAX_DEGREES = 3

const EducationFields = ({
  constantsData,
  control,
  errors,
  deleteField,
  editing,
}: PropsType): ReactElement => {
  /****************************************
   * PROPS
   *****************************************/

  const programDegreeOptions =
    constantsData?.constants.programDegreeCodes.map(
      (x: { key: string; value: string }) => {
        return {
          value: x.key,
          label: x.value,
        }
      },
    ) || []

  const programTypeOptions =
    constantsData?.constants.programTypeCodes.map(
      (x: { key: string; value: string }) => {
        return {
          value: x.key,
          label: x.value,
        }
      },
    ) || []

  /****************************************
   * STATE
   *****************************************/

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalIndex, setModalIndex] = useState<number | undefined>()

  /****************************************
   * REFS
   *****************************************/

  const degreeTypeRef = useRef<HTMLInputElement | null>(null)
  const programTypeRef = useRef<HTMLInputElement | null>(null)
  const schoolNameRef = useRef<HTMLInputElement | null>(null)
  const graduationYearRef = useRef<HTMLInputElement | null>(null)

  /******************************************
   * HOOKS
   ******************************************/
  const { fields, append, remove } = useFieldArray<EducationField>({
    control,
    name: 'Education',
  })

  /******************************************
   * RENDER
   ******************************************/

  return (
    <>
      {/* Banner */}
      <Grid xs={12} item>
        <Banner fullWidth icon="lock-on">
          None of the following information will ever be visible on your public
          profile.
        </Banner>
      </Grid>

      {constantsData && (
        <>
          {/* Field Array */}
          {fields.map((item, index) => (
            <Fragment key={`${item.id || ''}_${index}`}>
              {index > 0 && (
                <Grid xs={12} item>
                  <Divider width="100%" color="#DBD9D6" height="1px" />
                </Grid>
              )}

              {/* Degree Type */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  name={`Education[${index}].degreeType`}
                  defaultValue={item.degreeType}
                  onFocus={() => degreeTypeRef?.current?.focus()}
                  render={({ onChange, onBlur, value, name }) => (
                    <Select
                      native
                      readOnly={!editing}
                      name={name}
                      label="Degree"
                      placeholder={strings.PROFILE.ACCESS.DEGREE}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value as string}
                      options={programDegreeOptions}
                      inputRef={degreeTypeRef}
                      error={!!errors?.Education?.[index]?.degreeType}
                      errorText={
                        errors?.Education?.[index]?.degreeType?.message
                      }
                    />
                  )}
                />
              </Grid>

              {/* Program Type */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  name={`Education[${index}].programType`}
                  defaultValue={item.programType}
                  onFocus={() => programTypeRef?.current?.focus()}
                  render={({ onChange, onBlur, value, name }) => (
                    <Select
                      native
                      readOnly={!editing}
                      name={name}
                      label="Program Type"
                      placeholder={strings.PROFILE.ACCESS.PROGRAM}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value as string}
                      options={programTypeOptions}
                      inputRef={programTypeRef}
                      error={!!errors?.Education?.[index]?.programType}
                      errorText={
                        errors?.Education?.[index]?.programType?.message
                      }
                    />
                  )}
                />
              </Grid>

              {/* School Name */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  name={`Education[${index}].schoolName`}
                  defaultValue={item.schoolName}
                  onFocus={() => schoolNameRef?.current?.focus()}
                  render={({ onChange, onBlur, value, name }) => (
                    <InputText
                      name={name}
                      readOnly={!editing}
                      label="School Name"
                      placeholder={strings.PROFILE.ACCESS.SCHOOL}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value as string}
                      inputRef={schoolNameRef}
                      error={!!errors?.Education?.[index]?.schoolName}
                      errorText={
                        errors?.Education?.[index]?.schoolName?.message
                      }
                    />
                  )}
                />
              </Grid>

              {/* Graduation Year */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  name={`Education[${index}].graduationYear`}
                  defaultValue={item.graduationYear}
                  onFocus={() => graduationYearRef?.current?.focus()}
                  render={({ onChange, onBlur, value, name }) => (
                    <InputText
                      name={name}
                      readOnly={!editing}
                      label="Graduation Year"
                      placeholder={strings.PROFILE.ACCESS.YEAR}
                      mask={'9999'}
                      maskplaceholder={null}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value as string}
                      inputRef={graduationYearRef}
                      error={!!errors?.Education?.[index]?.graduationYear}
                      errorText={
                        errors?.Education?.[index]?.graduationYear?.message
                      }
                    />
                  )}
                />
              </Grid>

              {/* ID */}
              <Controller
                control={control}
                name={`Education[${index}].educationId`}
                defaultValue={item.educationId}
                render={(props) => <input hidden type="text" {...props} />}
              />

              {/* Remove Button */}
              {fields.length > 1 && editing && (
                <>
                  <Grid xs={12} item>
                    <Button
                      // onClick={() => deleteField(index, remove)}
                      onClick={() => {
                        setModalOpen(true)
                        setModalIndex(index)
                      }}
                      size="medium"
                      variant="secondary"
                    >
                      Remove
                    </Button>
                  </Grid>
                </>
              )}
            </Fragment>
          ))}

          {/* Add More */}
          {fields.length < MAX_DEGREES && editing && (
            <>
              <Grid xs={12} item>
                <Divider width="100%" color="#DBD9D6" height="1px" />
              </Grid>

              <Grid xs={12} item>
                <Button
                  onClick={() =>
                    append({
                      degreeType: '',
                      programType: '',
                      schoolName: '',
                      graduationYear: '',
                      educationId: '',
                    })
                  }
                  size="medium"
                >
                  Add More
                </Button>
              </Grid>
            </>
          )}
        </>
      )}
      {modalIndex !== undefined && (
        <RemoveModal
          isOpen={modalOpen}
          setIsOpen={setModalOpen}
          submitHandler={() => deleteField(modalIndex, remove)}
        />
      )}
    </>
  )
}

export default EducationFields
